import { useSyncExternalStore } from 'react'

import type { PersonalSettings } from '@ebalance/proto/bffuser/v1/setting_model_pb'
import { useQueryClient } from '@tanstack/react-query'

type SaveState = {
  tokens: Set<symbol>
  listeners: Set<() => void>
}
const saveStates = new WeakMap<object, SaveState>()
type SaveCoordinator = {
  accepting: boolean
  tail: Promise<void>
  persistedSettings?: PersonalSettings
}
const coordinators = new WeakMap<object, SaveCoordinator>()

function coordinatorFor(owner: object): SaveCoordinator {
  const existing = coordinators.get(owner)
  if (existing !== undefined) return existing
  const created: SaveCoordinator = { accepting: true, tail: Promise.resolve() }
  coordinators.set(owner, created)
  return created
}

function saveStateFor(owner: object): SaveState {
  const existing = saveStates.get(owner)
  if (existing !== undefined) return existing
  const created = {
    tokens: new Set<symbol>(),
    listeners: new Set<() => void>(),
  }
  saveStates.set(owner, created)
  return created
}

function emit(state: SaveState): void {
  for (const listener of state.listeners) listener()
}

export function beginPersonalSettingsSave(owner: object, token: symbol): void {
  const state = saveStateFor(owner)
  if (state.tokens.has(token)) return
  state.tokens.add(token)
  emit(state)
}

export function endPersonalSettingsSave(owner: object, token: symbol): void {
  const state = saveStateFor(owner)
  if (!state.tokens.delete(token)) return
  emit(state)
}

export function hasOtherPersonalSettingsSave(
  owner: object,
  token: symbol,
): boolean {
  const tokens = saveStateFor(owner).tokens
  return tokens.size > (tokens.has(token) ? 1 : 0)
}

export function recordPersistedPersonalSettings(
  owner: object,
  settings: PersonalSettings | undefined,
): void {
  if (settings !== undefined) {
    const coordinator = coordinatorFor(owner)
    coordinator.persistedSettings = settings
    coordinator.accepting = true
  }
}

class PersonalSettingsSaveCancelledError extends Error {}

export function isPersonalSettingsSaveCancelled(error: unknown): boolean {
  return error instanceof PersonalSettingsSaveCancelledError
}

export function enqueuePersonalSettingsSave<T>(
  owner: object,
  save: (persistedSettings: PersonalSettings | undefined) => Promise<T>,
  getPersistedSettings: (result: T) => PersonalSettings | undefined,
): Promise<T> {
  const coordinator = coordinatorFor(owner)
  const result = coordinator.tail.then(() => {
    if (!coordinator.accepting) {
      throw new PersonalSettingsSaveCancelledError()
    }
    return save(coordinator.persistedSettings)
  })
  coordinator.tail = result.then(
    (value) => {
      const settings = getPersistedSettings(value)
      if (settings !== undefined) coordinator.persistedSettings = settings
    },
    (error) => {
      if (!isPersonalSettingsSaveCancelled(error)) coordinator.accepting = false
    },
  )
  return result
}

export function useIsPersonalSettingsSaving(): boolean {
  const owner = useQueryClient()
  const state = saveStateFor(owner)
  return useSyncExternalStore(
    (listener) => {
      state.listeners.add(listener)
      return () => state.listeners.delete(listener)
    },
    () => state.tokens.size > 0,
    () => false,
  )
}
